import React, { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Container } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useParams } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '../../store';
import { addAction, addCategory } from '../../store/mobileSlice';

import Search from '../SearchMain/Search';
import ListTypeMobile from '../../components/ListTypeMobile/ListTypeMobile';
import SelectLocationsOnMobile from '../../components/SelectLocationsOnMobile/SelectLocationsOnMobile';
import SelectPrice from '../../components/SelectPrice/SelectPrice';

import { currencyFormat } from '../../utils/index';
import { useQuery } from '../../hooks/useQuery';

export interface SelectedPopupType {
  actions: boolean;
  category: boolean;
  locations: boolean;
  price: boolean;
  isSubApp: boolean;
}

const locationTypeLanguage = {
  th: 'จังหวัด',
  en: 'Province',
  cn: '省',
};

export type LanguageType = {
  language: 'th' | 'en' | 'cn';
};

type ListSelectSearchNameType = {
  id: number;
  name: string;
  isCheck: 'actions' | 'category' | 'locations' | 'price';
};

type ListSelectSearchType = {
  th: ListSelectSearchNameType[];
  en: ListSelectSearchNameType[];
  cn: ListSelectSearchNameType[];
};

const listSelectSearch: ListSelectSearchType = {
  th: [
    { id: 1, name: 'ต้องการ', isCheck: 'actions' },
    { id: 2, name: 'หมวดหมู่', isCheck: 'category' },
    { id: 3, name: 'ที่ตั้ง', isCheck: 'locations' },
    { id: 4, name: 'ราคา', isCheck: 'price' },
  ],
  en: [
    { id: 1, name: 'Action', isCheck: 'actions' },
    { id: 2, name: 'Category', isCheck: 'category' },
    { id: 3, name: 'Location', isCheck: 'locations' },
    { id: 4, name: 'Price', isCheck: 'price' },
  ],
  cn: [
    { id: 1, name: '需求', isCheck: 'actions' },
    { id: 2, name: '类型', isCheck: 'category' },
    { id: 3, name: '地点', isCheck: 'locations' },
    { id: 4, name: '价格', isCheck: 'price' },
  ],
};

const searchName = {
  th: 'ค้นหา',
  en: 'Search',
  cn: '搜索',
};

const currencyName = {
  th: 'บาท',
  en: 'THB',
  cn: '泰铢',
};

const defaultSearchState = {
  th: {
    actions: 'รวม',
    category: 'อสังหา',
  },
  en: {
    actions: 'all',
    category: 'real estate',
  },
  cn: {
    actions: '全部',
    category: '房地产',
  },
};

const SearchMobile = () => {
  const { language } = useParams<LanguageType>();
  const stateMobile = useAppSelector((state) => state.mobile);
  const dispatch = useAppDispatch();
  const query = useQuery();

  const [selectedPopup, setSelectedPopup] = useState<SelectedPopupType>({
    actions: false,
    category: false,
    locations: false,
    price: false,
    isSubApp: false,
  });
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (language === 'th' || language === 'en' || language === 'cn') {
      dispatch(addAction(defaultSearchState[language].actions));
      dispatch(addCategory(defaultSearchState[language].category));
    }

    if (query.get('isSubApp')) {
      console.log('I am sub app');
      setSelectedPopup({
        ...selectedPopup,
        isSubApp: true,
      });

      if (query.has('target')) {
        setTarget(query.get('target') || '9assetApp');
      } else {
        setTarget('9assetApp');
      }
    }
  }, []);

  const handlePopupSelected = (key: string, value: boolean) => {
    setSelectedPopup((prevState) => ({ ...prevState, [key]: value }));
  };

  const showSelectValue = (list: ListSelectSearchNameType) => {
    if (language === undefined) return;

    if (list.isCheck === 'price' && stateMobile['price'].priceMin !== 0 && stateMobile['price'].priceMax !== 0) {
      return (
        <>{`${currencyFormat(stateMobile['price'].priceMin)} - ${currencyFormat(stateMobile['price'].priceMax)} ${
          currencyName[language]
        }`}</>
      );
    }

    if (
      stateMobile[list.isCheck] &&
      list.isCheck === 'locations' &&
      (language === 'th' || language === 'en' || language === 'cn')
    ) {
      return (
        <div className="text-lg">{stateMobile[list.isCheck].replace(`${locationTypeLanguage[language]} > `, '')}</div>
      );
    }

    if (stateMobile[list.isCheck] && list.isCheck !== 'price') {
      return <div className="text-lg">{stateMobile[list.isCheck]}</div>;
    }

    return <div className="text-slate-600">{list.name}</div>;
  };

  const handleOnClose = () => {
    if (selectedPopup.isSubApp) {
      const msg = {
        target: target || '9assetApp',
        type: 'mobileSearch-goto-close',
        value: undefined,
      };

      console.log('mobileSearch-goto-close:', msg);
      window.parent.postMessage(msg, '*');
    }
  };

  const onSubmitSearch = () => {
    if (language === 'th' || language === 'en' || language === 'cn') {
      const arrayLocations = stateMobile.locations
        .replace(`${locationTypeLanguage[language]} > `, '')
        .trim()
        .split('>');
      let resultLocation = '';

      arrayLocations.forEach((location) => {
        resultLocation += `/${location.trim()}`;
      });

      let newurl = `${process.env.REACT_APP_HOME_URL || 'https://9asset.com/'}`;
      if (language != 'th') {
        newurl += `${language}/`;
      }

      newurl += `${stateMobile.actions ? stateMobile.actions : defaultSearchState[language].actions}/`;
      newurl += `${stateMobile.category ? stateMobile.category : defaultSearchState[language].category}`;
      newurl += `${resultLocation}`;
      newurl += `${stateMobile.price.priceMin !== 0 ? `?startPrice=${stateMobile.price.priceMin}` : ''}`;
      newurl += `${stateMobile.price.priceMax !== 0 ? `&endPrice=${stateMobile.price.priceMax}` : ''}`;

      if (selectedPopup.isSubApp) {
        const msg = {
          target: target || '9assetApp',
          type: 'mobileSearch-goto',
          value: newurl,
          payload: {
            action: stateMobile.actions || defaultSearchState[language].actions,
            category: stateMobile.category || defaultSearchState[language].category,
            location: resultLocation,
            price: {
              min: stateMobile.price.priceMin || null,
              max: stateMobile.price.priceMax || null,
            },
          },
        };

        console.log('mobileSearch-goto:', msg);
        window.parent.postMessage(msg, '*');
      } else {
        window.location.replace(newurl);
      }
    }
  };
  if (language === undefined) return <div>Loading...</div>;

  return (
    <>
      <Container className="relative">
        <div className="flex justify-end">
          <IconButton onClick={handleOnClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className="relative">
          <div className="absolute w-full">
            <Search />
          </div>
        </div>
        <div className="mt-16">
          {listSelectSearch[language].map((list) => (
            <div key={list.id}>
              <div
                className="border px-4 rounded flex justify-between items-center my-2 bg-[#f8e8de] border-[#f8e8de]"
                onClick={() => {
                  setSelectedPopup((prevState) => ({ ...prevState, [list.isCheck]: true }));
                }}>
                {showSelectValue(list)}
                <IconButton>
                  <KeyboardArrowDownIcon />
                </IconButton>
              </div>
              {list.isCheck === 'actions' && selectedPopup.actions && (
                <ListTypeMobile listType={list.isCheck} handlePopupSelected={handlePopupSelected} language={language} />
              )}
              {list.isCheck === 'category' && selectedPopup.category && (
                <ListTypeMobile listType={list.isCheck} handlePopupSelected={handlePopupSelected} language={language} />
              )}
              {list.isCheck === 'locations' && selectedPopup.locations && (
                <SelectLocationsOnMobile
                  open={selectedPopup[list.isCheck]}
                  listType={list.isCheck}
                  handlePopupSelected={handlePopupSelected}
                  language={language}
                />
              )}
              {list.isCheck === 'price' && selectedPopup.price && (
                <SelectPrice
                  listType={list.isCheck}
                  open={selectedPopup[list.isCheck]}
                  handlePopupSelected={handlePopupSelected}
                  language={language}
                />
              )}
            </div>
          ))}
        </div>
        <button
          className="border rounded flex justify-center items-center w-full py-2 mt-6 bg-[#f4762a] text-white"
          onClick={() => onSubmitSearch()}>
          {searchName[language]}
        </button>
      </Container>
    </>
  );
};

export default SearchMobile;
